<template>
  <div>
    <template v-if="template != null">
      <label class="label">{{ $t("labels.activeTemplate") }}</label>
      <button-item :icons="['x']" @click:x="deactivateTemplate">
        {{ template?.title }}
      </button-item>

      <input-switch
        class="mt-4"
        :label="$t('labels.placeholdersVisible')"
        :value="placeholdersVisible"
        @update:value="placeholdersVisible = $event"
      />
      <input-switch
        :label="$t('labels.netAreasVisible')"
        :value="areasVisible"
        @update:value="areasVisible = $event"
      />
    </template>

    <template v-if="template != null">
      <div class="label mt-4">
        {{ $t("words.netAreaName", 3) }}
      </div>
      <div v-for="area in areas" :key="'area' + area.id" class="elpos">
        <input-text
          ref="areaFields"
          :placeholder="$t('words.netAreaName')"
          :value="area.title"
          @update:value="updateArea(area, 'title', $event)"
        />
      </div>

      <drawer v-if="developerMode" title="Platzhalter" :nested="true">
        <label class="label">{{ $t("labels.coordinates") }}</label>
        <div
          v-for="position in targetPositions"
          :key="position.id"
          class="elpos"
        >
          <input-number
            v-model:value.number="position.x"
            class="elpos__x"
            :step="10"
            :min="-5000"
          />
          <input-number
            v-model:value.number="position.y"
            class="elpos__y"
            :step="10"
            :min="-5000"
          />
          <input-number
            v-model:value.number="position.areaId"
            type="number"
            class="elpos__areaId"
          />
        </div>
      </drawer>
    </template>

    <label v-if="!template" class="label">
      {{ $t("actions.chooseX", [$t("objectNames.template")]) }}
    </label>
    <button-list
      v-if="!template"
      :items="templates"
      prop="title"
      @select="activateTemplate($event.id)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { showConfirm } from '@core/ui/alert/alert';

import InputSwitch from '@core/input/InputSwitch.vue';
import ButtonList from "@core/ui/ButtonList.vue";
import InputNumber from "@core/input/InputNumber.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import InputText from '@core/input/InputText.vue';
import Drawer from '@core/ui/DrawerBlock.vue';

import { useSceneStore } from './scenes.store';
import { useAreaStore } from '../area/areas.store';
import { useTemplateStore } from '../template/templates.store';
import { useSettingsStore } from '@editor/store/settings.store';

import type { AreaItem } from '../area/AreaItem';

const sceneStore = useSceneStore();
const areaStore = useAreaStore();
const templateStore = useTemplateStore();

const { t } = useI18n();


// ==================
// TEMPLATE MENU

const areas = computed(() => {
  return areaStore.orderedItems.filter(area => area.sceneId === sceneStore.activeScene?.id);
});

const templates = computed(() => {
  return templateStore.templates;
});

const template = computed(() => {
  return templateStore.template;
});

const targetPositions = computed(() => {
  if (template.value == null) return null;
  return template.value.targetPositions;
});

const areasVisible = computed({
  get() {
    return sceneStore.activeScene?.areasVisible ?? true;
  },
  set(value: boolean) {
    const activeScene = sceneStore.activeScene;
    if (activeScene == null) return;
    sceneStore.update({
      id: activeScene.id,
      areasVisible: value
    });
  }
});

const placeholdersVisible = computed({
  get() {
    return sceneStore.activeScene?.placeholdersVisible ?? false;
  },
  set(value: boolean) {
    const activeScene = sceneStore.activeScene;
    if (activeScene == null) return;
    sceneStore.update({
      id: activeScene.id,
      placeholdersVisible: value
    });
  }
});


function activateTemplate(templateId = 0) {
  templateStore.activate(templateId);
}
async function deactivateTemplate() {
  if (await showConfirm(t("questions.confirmDeactivatingTemplate"))) {
    templateStore.deactivate();
  }
}


// TODO: connect area name clicks to this?
// const areaFields = useTemplateRef("areaFields");
// function selectAreaSettings(areaId: number) {
//   nextTick(() => {
//     const field = areaFields.value?.[areaId];
//     field?.focus();
//   });
// }

function updateArea<K extends keyof AreaItem>(
  area: { type: "area"; id: number },
  key: K,
  value: AreaItem[K]
) {
  areaStore.update({
    id: area.id,
    [key]: value
  });
}

// ==================
// DEVELOPER MODE

const developerMode = computed(() => {
  return useSettingsStore().developerMode;
});
</script>

<style lang="scss">
.scene-template-panel {
  &__button {
    flex-direction: row;
    white-space: nowrap;
    gap: 0.5rem;
  }
  &__buttontext {
    font-size: 1rem;
    font-weight: 400;
    overflow: hidden;
    max-width: 12rem;
    text-overflow: ellipsis;
  }

  &__panel {
    padding: 1rem;
    align-items: center;
    overflow: hidden;
    background-color: var(--color-background-deep);
    border-radius: 0.5rem;
    box-shadow: 0px 1px 7px var(--color-shadow-3);
  }

  &__list-item {
    background-color: var(--color-background-deep);
    display: flex;
    width: 100%;
    text-align: left;

    overflow: hidden;
    text-overflow: ellipsis;
    min-height: 2em;

    &:not(:disabled):hover,
    &:not(:disabled):focus-visible,
    &:not(:disabled):focus-within {
      background-color: var(--color-background);
    }
    &:disabled {
      color: var(--color-text-lighter);
    }
    &.-selected {   
      background-color: var(--color-primary-light);
      font-weight: 600;
      color: var(--color-text);

      &:hover {
        background-color: var(--color-primary-light);
      }
    }
  }
  &__list-item-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.25rem 1rem;
  }

  &__action-icon {
    opacity: 0;
    font-size: 1.1rem;
    pointer-events: auto;
    :hover > &,
    :focus-within > & {
      opacity: 1;
    }

    .lucide-icon {
      pointer-events: none;
    }
  }
}
</style>